import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CRow, CCol, CCard, CCardHeader, CCardBody,
  CFormInput, CFormSelect, CButton, CBadge,
  CProgress, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CAlert, CSpinner, CModal, CModalHeader, CModalTitle, CModalBody
} from '@coreui/react';

// Util: cor por status
const statusBadgeColor = (status) => ({ queued: 'secondary', running: 'info', done: 'success', error: 'danger', partial: 'warning' }[status] || 'secondary');
const pctColor = (status) => (status === 'done' ? 'success' : status === 'error' ? 'danger' : status === 'running' ? 'info' : 'secondary');

// Util: mensagem amigável de erro vinda do Axios/Backend
const apiErrorToMessage = (err) => {
  if (!err) return 'Erro inesperado';
  const any = err;
  const fromHeader = any?.response?.headers?.['omieapi-error'];
  const faultString = any?.response?.data?.faultstring;
  const plainMsg = any?.message;
  if (faultString) return faultString;
  if (fromHeader) return fromHeader.replace(/\u00E3/g, 'ã');
  if (plainMsg) return plainMsg;
  try { return JSON.stringify(any); } catch { return 'Erro desconhecido'; }
};

// Deriva UF do nome, caso backend antigo não envie coluna "uf"
const deriveUF = (nomeArquivo) => {
  const m = /^([A-Z]{2})[_-]/i.exec(nomeArquivo || '');
  return m ? m[1].toUpperCase() : '';
};

// Zona de Upload (drag & drop + input)
function UploadZone({ disabled, onFiles }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter(f => /\.(xlsx|xls)$/i.test(f.name));
    if (files.length) onFiles(files);
  };
  const onChange = (e) => {
    const files = Array.from(e.target.files || []).filter(f => /\.(xlsx|xls)$/i.test(f.name));
    if (files.length) onFiles(files);
    e.target.value = '';
  };
  return (
    <div
      onDragOver={(e)=>{e.preventDefault(); if(!disabled) setDragOver(true);}}
      onDragLeave={()=>setDragOver(false)}
      onDrop={onDrop}
      onClick={()=> !disabled && inputRef.current?.click()}
      style={{
        border: '2px dashed var(--cui-gray-400)',
        borderRadius: 12,
        padding: 24,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: dragOver ? 'rgba(32,168,216,0.08)' : 'transparent',
        transition: 'background .15s ease'
      }}
    >
      <div style={{fontWeight: 600, marginBottom: 6}}>Arraste e solte suas planilhas aqui</div>
      <div className="text-muted" style={{marginBottom: 10}}>ou clique para selecionar (.xlsx, .xls)</div>
      <CButton color="dark" size="sm" disabled={disabled}>Selecionar arquivos</CButton>
      <input ref={inputRef} type="file" multiple accept=".xlsx,.xls" style={{display:'none'}} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export default function PedidosDashboard() {
  // filtros topo
  const [ufOptions, setUfOptions] = useState([]);
  const [filtroUF, setFiltroUF] = useState('');
  const [filtroStatusPlan, setFiltroStatusPlan] = useState('');

  // execuções
  const [execucoes, setExecucoes] = useState([]); // lista (se endpoint existir)
  const [selectedExecId, setSelectedExecId] = useState('');
  const [execucao, setExecucao] = useState(null);

  // controle
  const [loadingList, setLoadingList] = useState(false);
  const [loadingExec, setLoadingExec] = useState(false);
  const pollingRef = useRef(null);
  const notifiedErrorsRef = useRef(new Set()); // evita toasts duplicados por planilha

  // toasts
  const [alerts, setAlerts] = useState([]);
 const pushAlert = (msg, color='danger') => setAlerts(a => [...a, { id: Math.random().toString(36).slice(2), msg, color }]);

  // ---------- API helpers ----------
  const fetchUFs = async () => {
    try {
      const r = await fetch('https://integradorbaoba.masterdaweb.net/api/pedidos/ufs');
      if (r.ok) {
        const arr = await r.json();
        if (Array.isArray(arr)) setUfOptions(arr);
      }
    } catch {}
  };

  const fetchExecucoes = async () => {
    setLoadingList(true);
    try {
      const q = new URLSearchParams({ limit: '30', uf: filtroUF || '' });
      const r = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/execucoes?${q.toString()}`);
      if (r.ok) {
        const data = await r.json();
        setExecucoes(Array.isArray(data) ? data : []);
      } else {
        setExecucoes([]); // endpoint pode não existir
      }
    } catch {
      setExecucoes([]);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchExecucao = async (id, startPolling=true) => {
    if (!id) return;
    setLoadingExec(true);
    try {
      const r = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/status/${id}`);
      if (!r.ok) throw new Error('Falha ao buscar execução');
      const data = await r.json();
      setExecucao(data);
      // polling
      if (startPolling) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (['queued','running'].includes(data.status)) {
          pollingRef.current = setInterval(async () => {
            const rr = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/status/${id}`);
            const ex = await rr.json();
            setExecucao(ex);
            // notificar erros novos por planilha
            (ex.importacao_planilhas||[]).forEach(p => {
              if (p.status === 'error' && !notifiedErrorsRef.current.has(p.id)) {            
                pushAlert(`Planilha #${p.id}: ${p.mensagem || 'falha ao processar.'}`)
              }
            });
            if (!['queued','running'].includes(ex.status)) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }, 1800);
        }
      }
    } catch (e) {
      pushAlert('Não foi possível obter a execução.','danger');
    } finally {
      setLoadingExec(false);
    }
  };

  const fetchEventos = async (planilhaId) => {
    const r = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/planilha/${planilhaId}/eventos`);
    if (!r.ok) return [];
    return r.json();
  };

  // ---------- effects ----------
  useEffect(() => { fetchUFs(); }, []);
  useEffect(() => { fetchExecucoes(); }, [filtroUF]);
  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // ---------- Upload ----------
  const onFilesSelected = async (files) => {
    if (!files?.length) return;
    const invalid = files.filter(f => !/\.(xlsx|xls)$/i.test(f.name));
    if (invalid.length) {
      pushAlert('Arquivos inválidos. Envie apenas .xlsx ou .xls','warning');   
      return;
    }

    try {
      const fd = new FormData();
      files.forEach(f => fd.append('file', f));

      const resp = await fetch('https://integradorbaoba.masterdaweb.net/api/pedidos/EnviaPedidosMulti', { method:'POST', body: fd });
      if (!resp.ok) throw new Error(await resp.text());
      const { execucaoId } = await resp.json();

      setSelectedExecId(String(execucaoId));
      pushAlert(`Carga iniciada. Execução #${execucaoId} criada.`, 'success');

      await fetchExecucao(execucaoId, true);
      await fetchExecucoes(); // atualizar lista
    } catch (err) {
      pushAlert(`Erro ao iniciar a carga ${apiErrorToMessage(err)} criada.`, 'danger');
   
    }
  };

  // ---------- Derived ----------
  const planilhasFiltradas = useMemo(() => {
    const list = execucao?.importacao_planilhas || [];
    return list
      .filter(p => !filtroUF || ((p.uf || deriveUF(p.nome_arquivo)) === filtroUF))
      .filter(p => !filtroStatusPlan || p.status === filtroStatusPlan)
      .sort((a,b) => (a.id < b.id ? 1 : -1));
  }, [execucao, filtroUF, filtroStatusPlan]);

  // ---------- Modal Eventos ----------
  const [showEventos, setShowEventos] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [eventosLoading, setEventosLoading] = useState(false);
  const [planilhaFocus, setPlanilhaFocus] = useState(null);

  const openEventos = async (p) => {
    setPlanilhaFocus(p);
    setShowEventos(true);
    setEventosLoading(true);
    const evs = await fetchEventos(p.id);
    setEventos(evs);
    setEventosLoading(false);
  };

  const EventosTable = () => (
    <CTable striped hover responsive>
      <CTableHead>
        <CTableRow>
          <CTableHeaderCell style={{width: 140}}>Data</CTableHeaderCell>
          <CTableHeaderCell style={{width: 90}}>Tipo</CTableHeaderCell>
          <CTableHeaderCell style={{width: 110}}>Fase</CTableHeaderCell>
          <CTableHeaderCell style={{width: 70}}>Linha</CTableHeaderCell>
          <CTableHeaderCell>Mensagem</CTableHeaderCell>
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {(eventos||[]).length ? eventos.map((e,i) => (
          <CTableRow key={i}>
            <CTableDataCell>{e.created_at ? new Date(e.created_at).toLocaleString('pt-BR',{dateStyle:'short', timeStyle:'short'}) : '-'}</CTableDataCell>
            <CTableDataCell><CBadge color={statusBadgeColor(e.tipo==='ERROR'?'error': e.tipo==='WARN'?'partial':'running')}>{e.tipo}</CBadge></CTableDataCell>
            <CTableDataCell>{e.fase || '-'}</CTableDataCell>
            <CTableDataCell>{e.linha ?? '-'}</CTableDataCell>
            <CTableDataCell>{e.mensagem}</CTableDataCell>
          </CTableRow>
        )) : (
          <CTableRow><CTableDataCell colSpan={5} className="text-center text-muted">Nenhum evento</CTableDataCell></CTableRow>
        )}
      </CTableBody>
    </CTable>
  );

  // ---------- Render ----------

  const clampPct = (v) => Math.max(0, Math.min(100, Math.round(Number(v) || 0)));

  const calcPct = (p) => {
    // finalizado = barra cheia
    if (['done', 'error'].includes(p.status)) return 100;

    // preferir o campo vindo do backend, se existir
    const fromBackend = (p.progresso ?? null);

    // fallback por itens processados
    const fromItems = (p?.total_itens > 0)
      ? (p.itens_processados / p.total_itens) * 100
      : null;

    // escolha o que estiver disponível e normalize
    return clampPct(fromBackend ?? fromItems ?? 0);
  };

  return (
    <CRow>
      {/* TOASTER
      <CToaster ref={toaster} push={toasts.map(t => (
        <CToast key={t.id} autohide={true} visible color={t.color} className="text-white">
          <CToastHeader closeButton><strong className="me-auto">{t.title}</strong></CToastHeader>
          <CToastBody>{t.body}</CToastBody>
        </CToast>
      ))} /> */}

      <CCol md={5}>
        <CCard className="mb-4 shadow-sm" style={{borderRadius: 16}}>
          <CCardHeader>
            <strong>Carregar planilhas de pedidos</strong>
          </CCardHeader>
          <CCardBody>
            <UploadZone disabled={loadingExec} onFiles={onFilesSelected} />
            <div className="text-muted mt-2" style={{fontSize: 12}}>Suporta múltiplos arquivos por execução. Extensões: .xlsx, .xls</div>

            <div className="mt-3 d-flex gap-2">
              <CFormSelect value={filtroUF} onChange={(e)=>setFiltroUF(e.target.value)} style={{maxWidth: 160}}>
                <option value="">UF (todas)</option>
                {ufOptions.map(u => <option key={u} value={u}>{u}</option>)}
              </CFormSelect>
              <CFormSelect value={filtroStatusPlan} onChange={(e)=>setFiltroStatusPlan(e.target.value)} style={{maxWidth: 200}}>
                <option value="">Status (todas)</option>
                <option value="queued">Aguardando</option>
                <option value="running">Em andamento</option>
                <option value="done">Concluída (ok)</option>
                <option value="error">Concluída (erro)</option>
              </CFormSelect>
            </div>
          </CCardBody>
        </CCard>

        <CCard className="shadow-sm" style={{borderRadius: 16}}>
          <CCardHeader>
            <strong>Execuções recentes</strong> {loadingList && <CSpinner size="sm" className="ms-2" />}
          </CCardHeader>
          <CCardBody>
            <CFormSelect value={selectedExecId} onChange={(e)=>{setSelectedExecId(e.target.value); if(e.target.value) fetchExecucao(e.target.value,true);}}>
              <option value="">Selecione uma execução…</option>
              {execucoes.map(ex => (
                <option key={ex.id} value={ex.id}>
                  #{ex.id} • {ex.status} • {ex.total_planilhas} planilhas • {ex.inicio_processamento ? new Date(ex.inicio_processamento).toLocaleString('pt-BR',{dateStyle:'short', timeStyle:'short'}) : '-'}
                </option>
              ))}
            </CFormSelect>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md={7}>
        <CCard className="mb-4 shadow-sm" style={{borderRadius: 16}}>
          <CCardHeader>
            <strong>Status da execução</strong>
          </CCardHeader>
          <CCardBody>
            {!execucao && (
              <CAlert color="secondary">Selecione uma execução para visualizar o andamento.</CAlert>
            )}
            {execucao && (
              <>
                <CRow className="mb-3">
                  <CCol md={3}>
                    <div className="text-muted small">Execução</div>
                    <div className="fs-5 fw-semibold">#{execucao.id} <CBadge color={statusBadgeColor(execucao.status)}>{execucao.status}</CBadge></div>
                  </CCol>
                  <CCol md={3}>
                    <div className="text-muted small">Planilhas</div>
                    <div className="fs-5 fw-semibold">{execucao.planilhas_concluidas}/{execucao.total_planilhas}</div>
                  </CCol>
                  <CCol md={3}>
                    <div className="text-muted small">Sucessos</div>
                    <div className="fs-5 fw-semibold text-success">{execucao.sucessos_total||0}</div>
                  </CCol>
                  <CCol md={3}>
                    <div className="text-muted small">Falhas</div>
                    <div className="fs-5 fw-semibold text-danger">{execucao.falhas_total||0}</div>
                  </CCol>
                </CRow>

                <CTable striped hover responsive>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Arquivo</CTableHeaderCell>
                      <CTableHeaderCell>UF</CTableHeaderCell>
                      <CTableHeaderCell>Status</CTableHeaderCell>
                      <CTableHeaderCell>Progresso</CTableHeaderCell>
                      <CTableHeaderCell className="text-center">Itens</CTableHeaderCell>
                      <CTableHeaderCell className="text-center">OK</CTableHeaderCell>
                      <CTableHeaderCell className="text-center">Falhas</CTableHeaderCell>
                      <CTableHeaderCell>Duração</CTableHeaderCell>
                      <CTableHeaderCell>Ações</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {planilhasFiltradas.length ? planilhasFiltradas.map(p => {
                      const pct = calcPct(p);
                      return (
                        <CTableRow key={p.id}>
                          <CTableDataCell title={p.mensagem || ''}>{p.nome_arquivo}</CTableDataCell>
                          <CTableDataCell>{(p.uf || deriveUF(p.nome_arquivo)) || '-'}</CTableDataCell>
                          <CTableDataCell>
                            <CBadge color={statusBadgeColor(p.status)}>{p.status}</CBadge>
                          </CTableDataCell>

                          {/* PROGRESSO com clamp + max=100 */}
                          <CTableDataCell style={{ minWidth: 160 }}>
                            <CProgress
                              value={pct}
                              max={100}
                              color={pctColor(p.status)}
                              role="progressbar"
                              aria-valuenow={pct}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              style={{ minWidth: 140 }}
                            >
                              {pct}%
                            </CProgress>
                          </CTableDataCell>

                          <CTableDataCell className="text-center">
                            {(p.itens_processados || 0)}/{(p.total_itens || 0)}
                          </CTableDataCell>
                          <CTableDataCell className="text-center text-success">{p.sucessos || 0}</CTableDataCell>
                          <CTableDataCell className="text-center text-danger">{p.falhas || 0}</CTableDataCell>
                          <CTableDataCell>{p.duracao || '-'}</CTableDataCell>
                          <CTableDataCell>
                            <CButton size="sm" color="info" variant="outline" onClick={() => openEventos(p)}>
                              Eventos
                            </CButton>
                          </CTableDataCell>
                        </CTableRow>
                      );
                    }) : (
                      <CTableRow>
                        <CTableDataCell colSpan={9} className="text-center text-muted">
                          Sem planilhas para os filtros atuais
                        </CTableDataCell>
                      </CTableRow>
                    )}
                  </CTableBody>
                </CTable>

              </>
            )}
          </CCardBody>
        </CCard>
      </CCol>

      {/* Modal de eventos */}
      <CModal visible={showEventos} onClose={()=>setShowEventos(false)} size="xl">
        <CModalHeader>
          <CModalTitle>Eventos — {planilhaFocus?.nome_arquivo}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {eventosLoading ? (
            <CAlert color="info"><CSpinner size="sm"/> Carregando…</CAlert>
          ) : (
            <EventosTable />
          )}
        </CModalBody>
      </CModal>
    </CRow>
  );
}
